import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { rewriteLegacyAppUrl } from 'app/core/auth/legacy-route-url.util';
import { LanguagePreferenceService } from 'app/core/transloco/language-preference.service';
import { normalizeAvatarUrl } from 'app/core/user/avatar-url.util';
import { UserService } from 'app/core/user/user.service';
import {
    BehaviorSubject,
    catchError,
    Observable,
    of,
    switchMap,
    throwError,
} from 'rxjs';
import { User } from './user.model';
import { environment } from 'environments/environment';
import { Router } from '@angular/router';

export interface FirstLoginChangePasswordPayload {
    email: string;
    oldPassword: string;
    newPassword: string;
    repeatedNewPassword: string;
}

export interface OtpChangePasswordPayload {
    email: string;
    code: string;
    password: string;
    repeatedPassword: string;
}

/** Body of `PUT auth/change-password`, the one self-serve change for every signed-in role. */
export interface ChangeOwnPasswordPayload {
    oldPassword: string;
    newPassword: string;
    repeatedPassword: string;
}

/** POST `auth/register` body. `password` is omitted on the Google path (`google: true`). */
export interface RegisterPayload {
    email: string;
    password?: string;
    company_name: string;
    phone: string;
    name: string;
    google?: boolean;
}

export interface RegisterResponse {
    data?: {
        verified?: boolean;
    };
}

@Injectable({ providedIn: 'root' })
export class OurAuthService {
    private _apiLink = environment.apiLink;
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private _languagePreferenceService = inject(LanguagePreferenceService);
    private _isLoggedIn$ = new BehaviorSubject<boolean>(false);
    isLoggedIn$ = this._isLoggedIn$.asObservable();
    private _router = inject(Router);
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    set refreshToken(token: string) {
        localStorage.setItem('refreshToken', token);
    }

    get refreshToken(): string {
        return localStorage.getItem('refreshToken') ?? '';
    }

    set userRole(role: string) {
        localStorage.setItem('userRole', role);
    }

    get userRole(): string {
        return localStorage.getItem('userRole') ?? '';
    }

    /** Logo of the signed-in account's organization (`user_image`, saved at sign-in for every role), or `''` when it has none. */
    get orgLogo(): string {
        return normalizeAvatarUrl(localStorage.getItem('user_image'));
    }

    /** Organization Admin (`Role.user` on the API). Stored as `company` after sign-in. */
    get isOrgAdmin(): boolean {
        return this.userRole === 'company';
    }

    constructor() {
        const token = localStorage.getItem('accessToken');
        this._isLoggedIn$.next(!!token);
    }

    getCountriesCode() {
        return this._httpClient.get('https://restcountries.com/v2/all').pipe(
            switchMap((response: any) => {
                // `.filter` before `.map`, not a bare `map` with a conditional return: every
                // country without both `callingCodes` and `flag` used to fall out of the callback
                // as `undefined` and stay in the array as a hole, so any consumer reading
                // `.callingCode` off one would throw.
                const newResponse = response
                    .filter((item) => item.callingCodes && item.flag)
                    .map((item) => ({
                        callingCode: '+' + item.callingCodes[0],
                        flag: item.flag,
                        name: item.name,
                    }));

                return of(newResponse);
            })
        );
    }
    forgotPassword(data: any): Observable<any> {
        return this._httpClient.post(
            this._apiLink + 'auth/forget-password',
            data
        );
    }

    verifyCode(data: any): Observable<any> {
        return this._httpClient.post(this._apiLink + 'auth/otp/verify', data);
    }
    resendCode(data: any): Observable<any> {
        return this._httpClient.post(this._apiLink + 'auth/get-otp', data);
    }

    resetPassword(data: OtpChangePasswordPayload): Observable<unknown> {
        return this._httpClient.post(
            this._apiLink + 'auth/reset-password',
            data
        );
    }
    resetFirstPassword(data: FirstLoginChangePasswordPayload): Observable<unknown> {
        return this._httpClient.post(
            this._apiLink + 'auth/first-login/change-password',
            data
        );
    }
    /**
     * One endpoint for all three roles. The backend reads the acting role off the token, so this
     * no longer branches between the members, staff and users routes it used to pick from.
     */
    changePassword(data: ChangeOwnPasswordPayload): Observable<any> {
        return this._httpClient.put(this._apiLink + 'auth/change-password', data);
    }

    getUserByRefreshToken(token: string): Observable<any> {
        const headers = new HttpHeaders()
            .set('skip-interceptor', '')
            .set('x-lang', localStorage.getItem('language') || 'en')
            .set('x-deviceId', 'www');
        return this._httpClient
            .post(
                this._apiLink + 'auth/refresh-token',
                { token },
                { headers }
            )
            .pipe(
                switchMap((response: any) => {
                    const data = response.data as User;
                    this.accessToken = data.accessToken;
                    this.refreshToken = data.refreshToken;
                    if (data.role === 'member') {
                        this.userRole = 'manager';
                    } else if (data.role === 'staff') {
                        this.userRole = 'staff';
                    } else if (data.role === 'admin') {
                        this.userRole = 'admin';
                        // Drop tenant identity leftovers from a prior company/member session.
                        localStorage.removeItem('user_image');
                        localStorage.removeItem('user_name');
                    } else {
                        this.userRole = 'company';
                    }
                    if (data.email) {
                        localStorage.setItem('email', data.email);
                    }
                    this._authenticated = true;
                    this._userService.user = response.data;
                    this._languagePreferenceService.applyFromBackend(data.prefLang);
                    this._userService.applyTourCompletedFromAuth(data.tourCompleted);
                    this._isLoggedIn$.next(true);
                    if (this.userRole === 'manager' || this.userRole === 'staff') {
                        this._userService.loadMemberStaffProfile().subscribe();
                    }
                    return of(response);
                })
            );
    }

    signIn(credentials: any): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this.login(credentials).pipe(
            switchMap((response: any) => {
                this.establishSession(response);
                return of(response);
            })
        );
    }

    /**
     * The `auth/login` request on its own: nothing is stored. `signIn` follows it with
     * `establishSession`; the messenger app calls it directly so it can refuse a role first.
     */
    login(credentials: { email: string; password?: string; google?: boolean }): Observable<any> {
        return this._httpClient.post(this._apiLink + 'auth/login', credentials);
    }

    /**
     * Persist a successful `auth/login` response as the active session (tokens, role, identity,
     * brand colour, language). `signIn` uses it, and so does the messenger app, which must inspect
     * the role BEFORE any token is stored so an `admin` login never becomes a session there.
     */
    establishSession(response: any): void {
        this.accessToken = (response.data as User).accessToken;
        this.refreshToken = (response.data as User).refreshToken;
        // `ui_theme_color` is the interface color. Older APIs only send `primary_color`, which then
        // means the same thing (it is kept as an alias of the theme color for shipped mobile builds).
        this.saveColor(response.data.ui_theme_color || response.data.primary_color || '#3dc3fb');
        this.saveBrandColor(response.data.brand_color);
        if (response.data.role === 'member') {
            this.userRole = 'manager';
            localStorage.setItem('email', response.data.email);
            localStorage.setItem(
                'user_image',
                response.data.user_image
            );
            localStorage.setItem(
                'user_name',
                response.data.acc_name
            );
        } else if (response.data.role === 'staff') {
            this.userRole = 'staff';
            localStorage.setItem('email', response.data.email);
            localStorage.setItem(
                'user_image',
                response.data.user_image
            );
            localStorage.setItem(
                'user_name',
                response.data.acc_name
            );
        } else if (response.data.role === 'admin') {
            this.userRole = 'admin';
            localStorage.setItem('email', response.data.email);
            // Drop tenant identity leftovers from a prior company/member session.
            localStorage.removeItem('user_image');
            localStorage.removeItem('user_name');
        } else {
            this.userRole = 'company';
            localStorage.setItem('email', response.data.email);
            localStorage.setItem(
                'user_image',
                response.data.user_image
            );
            localStorage.setItem(
                'user_name',
                response.data.user_name
            );
        }

        (window as any).applyBrand?.();
        this._authenticated = true;
        this._userService.user = response.data;
        this._languagePreferenceService.applyFromBackend(response.data.prefLang);
        this._userService.applyTourCompletedFromAuth(response.data.tourCompleted);
        this._isLoggedIn$.next(true);
        if (this.userRole === 'manager' || this.userRole === 'staff') {
            this._userService.loadMemberStaffProfile().subscribe();
        }
    }

    /**
     * Apply the organization's UI theme color. The storage key stays `primary_color` because
     * `index.html` and the messenger app read it before Angular boots; it holds the theme color,
     * not the brand color.
     */
    saveColor(selectedColor) {
        localStorage.setItem('primary_color', selectedColor);
        // Update the CSS custom properties (--c1 and --c2) dynamically
        document.documentElement.style.setProperty('--c1', selectedColor);
        document.documentElement.style.setProperty(
            '--c2',
            this.adjustColorBrightness(selectedColor)
        ); // Optionally adjust color brightness for the second color

        // Optionally save the selected color to your backend if necessary
        // this.companyService.updateCompanyColor(selectedColor).subscribe();
    }

    /** Keep the organization's brand color for branded output rendered in the browser. */
    saveBrandColor(color: string | null | undefined): void {
        if (color) localStorage.setItem('brand_color', color);
        else localStorage.removeItem('brand_color');
    }

    /**
     * The organization's brand color, for branded output rendered in the browser (the chat PDF
     * export). A session started before the split has none stored; the theme color was the brand
     * until then, so it is the right fallback.
     */
    get brandColor(): string | undefined {
        return localStorage.getItem('brand_color')?.trim() || localStorage.getItem('primary_color')?.trim() || undefined;
    }

    // A utility function to adjust the brightness of the selected color (you can modify it)
    adjustColorBrightness(color: string): string {
        // This is a basic function to lighten the color. You can adjust it as needed.
        const colorHex = color.startsWith('#') ? color.slice(1) : color; // Remove # if present
        const r = parseInt(colorHex.substring(0, 2), 16);
        const g = parseInt(colorHex.substring(2, 4), 16);
        const b = parseInt(colorHex.substring(4, 6), 16);

        // Simple brightness adjustment by increasing RGB values
        const brightnessFactor = 1.2; // Factor to adjust the brightness
        const adjustedR = Math.min(Math.floor(r * brightnessFactor), 255);
        const adjustedG = Math.min(Math.floor(g * brightnessFactor), 255);
        const adjustedB = Math.min(Math.floor(b * brightnessFactor), 255);

        return `#${adjustedR.toString(16).padStart(2, '0')}${adjustedG
            .toString(16)
            .padStart(2, '0')}${adjustedB.toString(16).padStart(2, '0')}`;
    }

    signInWithGoogle() {
        const url = `${this._apiLink}login/google`;
        window.open(url, '_self');
    }

    private tokenExchangeEndpoint = '/api/auth/exchange-token';
    exchangeAuthCodeForToken(code: string): Observable<any> {
        // Send a POST request to your server with the authorization code
        return this._httpClient.post(this.tokenExchangeEndpoint, { code });
    }

    handleLogin(response: any): void {
        // Handle storing the access token and setting up the session
        // For example:
        localStorage.setItem('accessToken', response.accessToken);
        // Set up other user session details
    }

    signInUsingToken(): Observable<any> {
        this._authenticated = true;
        this._isLoggedIn$.next(true);
        return of(true);
    }

    signOut(): Observable<any> {
        const savedLang = localStorage.getItem('language');
        const savedPrefLang = localStorage.getItem('prefLang');

        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userRole');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('primary_color');
        localStorage.removeItem('brand_color');
        localStorage.removeItem('user_image');
        localStorage.removeItem('email');
        localStorage.removeItem('user_name');
        // localStorage.clear();
            window.dispatchEvent(new Event('brand:refresh'));

        if (savedLang) {
            localStorage.setItem('language', savedLang);
        }
        if (savedPrefLang) {
            localStorage.setItem('prefLang', savedPrefLang);
        }

        // Set the authenticated flag to false
        this._authenticated = false;
        this._isLoggedIn$.next(false);
        this._userService.clearPermissionsCache();

        // Return the observable
        return of(true);
    }

    signUp(user: RegisterPayload): Observable<RegisterResponse> {
        return this._httpClient.post<RegisterResponse>(this._apiLink + 'auth/register', user);
    }

    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    role(): Observable<string> {
        return of(this.userRole);
    }

    /** Default home route after sign-in when no explicit redirectURL is provided. */
    getDefaultAuthenticatedLandingUrl(): string {
        const role = this.userRole;
        if (role === 'chatting') {
            return '/chat';
        }
        if (role === 'admin') {
            return '/admin-users';
        }
        return '/overview';
    }

    /** Resolves post-login navigation, honoring redirectURL for tenant users only. */
    resolvePostLoginRedirectUrl(redirectUrl?: string | null): string {
        const role = this.userRole;
        if (role === 'chatting') {
            return '/chat';
        }
        if (role === 'admin') {
            return '/admin-users';
        }

        const requested = redirectUrl?.trim();
        if (!requested || requested === '/signed-in-redirect') {
            return '/overview';
        }

        return rewriteLegacyAppUrl(requested.startsWith('/') ? requested : `/${requested}`);
    }

    makeItOffline(): void {
        const url = `${this._apiLink}users/log-out`;
        const token = localStorage.getItem('accessToken'); // Retrieve token from localStorage
        const data = new Blob([JSON.stringify({ status: 'offline', token })], {
            type: 'application/json',
        });

        // Use navigator.sendBeacon for the log-out request
        if (navigator.sendBeacon) {
            // Option 1: Send token in the body if backend can handle it
            const isSent = navigator.sendBeacon(url, data);
        } else {
            // Fallback for environments where sendBeacon is unavailable
            fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`, // Set the Authorization header
                    'Content-Type': 'application/json', // Set content type
                },
                body: JSON.stringify({ status: 'offline' }), // JSON format required for headers in fetch
                keepalive: true, // Ensures the request stays alive briefly for it to complete
            })
                .then((response) => {})
                .catch(error =>
                    console.error('Logout request failed:', error)
                );
        }
    }
}
